import { useCallback, useEffect, useRef, useState } from "react";
import { apiClient } from "../../services/authService";
import type { LlmResponseRun, Product, Summary } from "./types";

/**
 * Hook que encapsula data fetching + handlers de CategoryReviewPage.
 * Extraído para mantener la página ≤500L (P7).
 */
export function useCategoryReviewData() {
  const [products, setProducts] = useState<Product[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [llmResponseFilter, setLlmResponseFilter] = useState<number | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [existingCategories, setExistingCategories] = useState<string[]>([]);
  const [categoryCounts, setCategoryCounts] = useState<Record<string, number>>(
    {},
  );
  const [llmResponses, setLlmResponses] = useState<LlmResponseRun[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importResult, setImportResult] = useState<any>(null);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [classifying, setClassifying] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info";
  } | null>(null);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set("page", String(page + 1));
      params.set("limit", String(rowsPerPage));
      if (search) params.set("search", search);
      if (categoryFilter) params.set("category", categoryFilter);
      if (sourceFilter) params.set("source", sourceFilter);
      if (llmResponseFilter)
        params.set("llmResponseId", String(llmResponseFilter));
      const res = (await apiClient.get(
        `/forecasting/category-review?${params}`,
      )) as { data: { data: Product[]; total: number } };
      setProducts(res.data.data);
      setTotal(res.data.total);
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          err.message ||
          "Error al cargar productos",
      );
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search, categoryFilter, sourceFilter, llmResponseFilter]);

  const fetchSummary = useCallback(async () => {
    try {
      const res = (await apiClient.get(
        "/forecasting/category-review/summary",
      )) as { data: Summary };
      setSummary(res.data);
    } catch {
      /* silent */
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    try {
      const res = (await apiClient.get(
        "/forecasting/category-review/categories",
      )) as { data: Array<{ category: string; count: number }> };
      setExistingCategories(res.data.map((c) => c.category));
      const counts: Record<string, number> = {};
      for (const c of res.data) counts[c.category] = c.count;
      setCategoryCounts(counts);
    } catch {
      /* silent */
    }
  }, []);

  const fetchLlmResponses = useCallback(async () => {
    try {
      const res = (await apiClient.get(
        "/forecasting/category-review/llm-responses",
      )) as { data: LlmResponseRun[] };
      setLlmResponses(res.data);
    } catch {
      /* silent */
    }
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);
  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);
  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);
  useEffect(() => {
    fetchLlmResponses();
  }, [fetchLlmResponses]);

  // Debounced search
  const [searchInput, setSearchInput] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Reset page on filter change
  useEffect(() => {
    setPage(0);
  }, [search, categoryFilter, sourceFilter, llmResponseFilter]);

  // Clear selection when page/filters change
  useEffect(() => {
    setSelectedIds(new Set());
  }, [page, rowsPerPage, search, categoryFilter, sourceFilter, llmResponseFilter]);

  const handleToggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedIds.size === products.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(products.map((p) => p.id)));
    }
  };

  const handleClassifySelected = async () => {
    if (selectedIds.size === 0) return;
    setClassifying(true);
    setError(null);
    try {
      const res = (await apiClient.post(
        "/forecasting/openai-classify-selected",
        { ids: Array.from(selectedIds) },
      )) as { data: { classified: number; unmatched: number; total: number } };
      setSelectedIds(new Set());
      fetchProducts();
      fetchSummary();
      fetchCategories();
      fetchLlmResponses();
      setError(null);
      setSnackbar({
        open: true,
        message: `Clasificacion completada: ${res.data.classified} clasificados, ${res.data.unmatched} sin clasificar de ${res.data.total} seleccionados.`,
        severity: "success",
      });
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          "Error al clasificar productos seleccionados",
      );
    } finally {
      setClassifying(false);
    }
  };

  const handleStartEdit = (product: Product) => {
    setEditingId(product.unified_product_id);
    setEditValue(product.unified_category || "");
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditValue("");
  };

  const handleSaveEdit = async (unifiedProductId: string) => {
    if (!editValue) return;
    setSaving(true);
    try {
      if (editValue === "__RESET__") {
        await apiClient.patch(
          `/forecasting/product-mapping/${unifiedProductId}/category/reset`,
        );
      } else {
        await apiClient.patch(
          `/forecasting/product-mapping/${unifiedProductId}/category`,
          { unifiedCategory: editValue },
        );
      }
      setEditingId(null);
      setEditValue("");
      fetchProducts();
      fetchSummary();
      fetchCategories();
    } catch (err: any) {
      setError(err?.response?.data?.message || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (categoryFilter) params.set("category", categoryFilter);
      if (sourceFilter) params.set("source", sourceFilter);
      const res = (await apiClient.get(
        `/forecasting/category-review/export?${params}`,
        { responseType: "blob" },
      )) as { data: Blob };
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = "category-review.csv";
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      setError("Error al exportar CSV");
    }
  };

  const handleImport = async (file: File) => {
    setImporting(true);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await apiClient.post(
        "/forecasting/category-review/import",
        formData,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setImportResult(res.data);
      fetchProducts();
      fetchSummary();
    } catch (err: any) {
      setImportResult({
        error: err?.response?.data?.message || "Error al importar",
      });
    } finally {
      setImporting(false);
    }
  };

  return {
    products,
    total,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    search,
    searchInput,
    setSearchInput,
    categoryFilter,
    setCategoryFilter,
    sourceFilter,
    setSourceFilter,
    llmResponseFilter,
    setLlmResponseFilter,
    loading,
    error,
    setError,
    summary,
    existingCategories,
    categoryCounts,
    llmResponses,
    editingId,
    editValue,
    setEditValue,
    saving,
    importOpen,
    setImportOpen,
    importResult,
    setImportResult,
    importing,
    fileInputRef,
    selectedIds,
    classifying,
    snackbar,
    setSnackbar,
    handleToggleSelect,
    handleSelectAll,
    handleClassifySelected,
    handleStartEdit,
    handleCancelEdit,
    handleSaveEdit,
    handleExport,
    handleImport,
  };
}
